import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Hidden from "@insite/mobius/Hidden";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css, FlattenSimpleInterpolation } from "styled-components";

type Props = WidgetProps & HasProduct;
const tabCss: FlattenSimpleInterpolation = css`
    background: ${(props: TabProps) => (props.selected ? "#036EB7" : "transparent")};
    color: ${(props: TabProps) => (props.selected ? "#fff" : "#036EB7")};
    font-weight: ${(props: TabProps) => (props.selected ? "normal" : "bold")};
    border: none;
    padding: 8px 16px;
    &:hover {
        cursor: pointer;
        border: none;
    }
` as FlattenSimpleInterpolation;

const tabContent = css`
    background: #f7fafc;
    body {
        background: #f7fafc;
    }
`;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    tabAttributeTypeLabelText?: DataTableCellProps;
    tabAttributeTypeValuesText?: DataTableCellProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    tabsWrapper?: InjectableCss;
    tableWrapper?: DataTableProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
        css: css`
            margin-bottom: 50px;
            [data-test-selector="sectionHeader"] {
                button[aria-expanded="true"] {
                    background: #036eb7;
                    border-width: 2px;
                    span {
                        color: #fff;
                        letter-spacing: 0.5px;
                    }
                }
                button {
                    background: transparent;
                    border-width: 2px;
                }
            }
            [data-test-selector="sectionPanel"] {
                border-width: 2px;
                background: #f7fafc;
                body {
                    background: transparent;
                }
            }
            ul[class^="UnorderedList"] {
                padding: 0 0 0 15px;
                margin: 5px 0 0 0;
            }
            table tr td:first-child {
                width: auto;
            }
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    tabsWrapper: {
        css: css`
            z-index: 0;
            position: relative;
            margin-bottom: 50px;
            ul[class^="UnorderedList"] {
                padding: 0 0 0 10px;
                margin: 0;
            }
            table tr td:first-child {
                width: auto;
            }
        `,
    },
    attributeTypeWrapper: {
        css: css`
            border: none;
            background: none;
        `,
    },
    tableWrapper: {
        css: css`
            width: auto;
        `,
    },
    tabAttributeTypeLabelText: {
        css: css`
            padding: 0;
            border: none;
            height: auto;
            font-weight: bold;
        `,
    },
    tabAttributeTypeValuesText: {
        css: css`
            padding: 0;
            border: none;
            height: auto;
            white-space: normal;
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({ product: { brand, attributeTypes, specifications } }) => {
    const renderingAttributes = brand || (attributeTypes && attributeTypes?.length > 0);
    const renderingSpecifications = specifications && specifications.length > 0;
    const attributesFirst = true;

    if (!renderingAttributes && !renderingSpecifications) {
        return null;
    }
    if (specifications?.length === undefined) {
        return null;
    }
    const attributesAccordion = renderingAttributes && (
        <ManagedAccordionSection
            title={translate("Attributes")}
            initialExpanded={attributesFirst || !renderingSpecifications}
            data-test-selector="productDetails_specifications_attributes"
            {...styles.specificationsAccordionSection}
        >
            <DataTable {...styles.tableWrapper}>
                <DataTableBody data-test-selector="attributesTable_tableBody">
                    {brand && (
                        <DataTableRow key={brand.id.toString()} {...styles.attributeTypeWrapper}>
                            <DataTableCell {...styles.tabAttributeTypeLabelText}>{translate("Brand")}</DataTableCell>
                            <DataTableCell {...styles.tabAttributeTypeValuesText}>
                                &nbsp;&nbsp;:&nbsp;&nbsp;
                            </DataTableCell>
                            <DataTableCell {...styles.tabAttributeTypeValuesText}>{brand.name}</DataTableCell>
                        </DataTableRow>
                    )}
                    {attributeTypes &&
                        attributeTypes.map(attributeType => (
                            <DataTableRow key={attributeType.id.toString()} {...styles.attributeTypeWrapper}>
                                <DataTableCell {...styles.tabAttributeTypeLabelText}>
                                    {attributeType.label || attributeType.name}
                                </DataTableCell>
                                <DataTableCell {...styles.tabAttributeTypeValuesText}>
                                    &nbsp;&nbsp;:&nbsp;&nbsp;
                                </DataTableCell>
                                <DataTableCell {...styles.tabAttributeTypeValuesText}>
                                    {attributeType
                                        .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                </DataTableBody>
            </DataTable>
        </ManagedAccordionSection>
    );

    const tabs: JSX.Element[] = [
        // eslint-disable-next-line react/jsx-key
    ];
    // eslint-disable-next-line eqeqeq
    const intialTab = specifications?.length != undefined ? specifications[0]?.name : "";
    const specificationTabs = (specifications: any[] | undefined) => {
        // eslint-disable-next-line no-lone-blocks
        {
            specifications?.map(specification => {
                const tabSpecification = specification;
                let htmlContent = specification.htmlContent;
                if (htmlContent.includes("<=")) {
                    htmlContent = htmlContent.replace(/<=/g, "&lt=");
                }
                if (htmlContent.includes("=<")) {
                    htmlContent = htmlContent.replace(/=</g, "=&lt");
                }
                if (htmlContent.includes(">=")) {
                    htmlContent = htmlContent.replace(/>=/g, "&gt=");
                }
                if (htmlContent.includes("=>")) {
                    htmlContent = htmlContent.replace(/=>/g, "=&gt");
                }
                tabs.push(
                    <Tab css={tabCss} headline={tabSpecification?.nameDisplay} tabKey={tabSpecification?.nameDisplay}>
                        {parse(htmlContent, parserOptions)}
                    </Tab>,
                );
            });
        }
    };

    const currentTab = intialTab;

    specificationTabs(specifications);

    return (
        <>
            <Hidden below="md" as="div" {...styles.tabsWrapper}>
                <TabGroup cssOverrides={{ tabContent }} current={currentTab}>
                    {tabs}
                </TabGroup>
            </Hidden>
            <Hidden above="sm" as="div">
                <Accordion
                    headingLevel={2}
                    data-test-selector="productDetails_specifications"
                    {...styles.specificationsAccordion}
                >
                    {renderingSpecifications &&
                        specifications?.map((specification, index) => {
                            let htmlContent = specification.htmlContent;
                            if (htmlContent.includes("<=")) {
                                htmlContent = htmlContent.replace(/<=/g, "&lt=");
                            }
                            if (htmlContent.includes("=<")) {
                                htmlContent = htmlContent.replace(/=</g, "=&lt");
                            }
                            if (htmlContent.includes(">=")) {
                                htmlContent = htmlContent.replace(/>=/g, "&gt=");
                            }
                            if (htmlContent.includes("=>")) {
                                htmlContent = htmlContent.replace(/=>/g, "=&gt");
                            }
                            return (
                                <ManagedAccordionSection
                                    initialExpanded={index === 0}
                                    key={specification.id.toString()}
                                    title={specification.nameDisplay}
                                    data-test-selector="productDetails_specifications_specification"
                                    {...styles.specificationsAccordionSection}
                                >
                                    {parse(htmlContent, parserOptions)}
                                </ManagedAccordionSection>)
                        })}
                </Accordion>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
